import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShieldCheck, ArrowRight, Battery } from 'lucide-react';

function AccessCodePage() {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    if (!code) {
      setError('Please enter an access code.');
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch('/access-codes.json');
      if (!response.ok) {
        throw new Error('Could not load access codes configuration.');
      }
      const validCodes = await response.json();

      if (validCodes.includes(code.trim().toUpperCase())) {
        sessionStorage.setItem('isAuthenticated', 'true');
        navigate('/');
      } else {
        setError('Invalid access code. Please try again.');
      }
    } catch (err) {
      console.error('Validation error:', err);
      setError('An error occurred. Please try again later.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-green-900 overflow-hidden font-car">
      {/* Animated Road Lines Background */}
      <div className="absolute inset-0 opacity-20">
        <div className="absolute top-0 left-1/4 w-1 h-full bg-gradient-to-b from-green-400 to-transparent animate-pulse"></div>
        <div className="absolute top-0 right-1/4 w-1 h-full bg-gradient-to-b from-green-400 to-transparent animate-pulse" style={{ animationDelay: '1s' }}></div>
        <div className="absolute top-0 left-1/2 w-1 h-full bg-gradient-to-b from-green-400 to-transparent animate-pulse" style={{ animationDelay: '2s' }}></div>
      </div>

      {/* Lightning Effects */}
      <div className="absolute top-20 right-10 opacity-30">
        <svg width="60" height="120" viewBox="0 0 60 120" className="animate-pulse">
          <path d="M30 0 L20 40 L35 40 L25 80 L40 80 L20 120" 
                stroke="#10b981" strokeWidth="2" fill="none" />
        </svg>
      </div>
      <div className="absolute top-40 left-20 opacity-20">
        <svg width="40" height="80" viewBox="0 0 40 80" className="animate-pulse" style={{ animationDelay: '1.5s' }}>
          <path d="M20 0 L15 25 L25 25 L18 50 L28 50 L15 80" 
                stroke="#10b981" strokeWidth="2" fill="none" />
        </svg>
      </div>

      {/* Interstate Logo Area */}
      <div className="absolute top-8 left-8 z-20">
        <div className="flex items-center gap-3">
          <div className="bg-green-500 p-3 rounded">
            <Battery className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold italic text-white tracking-wider">INTERSTATE</h1>
        </div>
      </div>

      <div className="flex items-center justify-center min-h-screen relative z-10">
      <div className="w-full max-w-2xl p-12 space-y-10 bg-gray-900/90 backdrop-blur-sm rounded-3xl shadow-2xl m-6 border border-green-500/30 relative overflow-hidden">
        {/* Subtle Pattern Overlay */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{
            backgroundImage: `repeating-linear-gradient(45deg, transparent, transparent 35px, rgba(16, 185, 129, 0.1) 35px, rgba(16, 185, 129, 0.1) 70px)`
          }}></div>
        </div>
        <div className="relative z-10">
        <div className="text-center">
          <h1 className="text-6xl font-black italic tracking-wide mb-4">
            <span className="text-white">INTERSTATE</span>
          </h1>
          <p className="mt-4 text-2xl font-light text-green-400">
            Presentation Access
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-8">
          <div>
            <label htmlFor="access-code" className="block text-lg font-semibold text-green-400 mb-4">
              Access Code
            </label>
            <div className="mt-2">
              <input
                id="access-code"
                type="text"
                value={code}
                onChange={(e) => setCode(e.target.value.toUpperCase())}
                placeholder="ENTER CODE"
                className="w-full px-6 py-5 text-xl text-white bg-gradient-to-br from-gray-800 to-gray-900 border border-green-500/30 rounded-2xl shadow-xl placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-green-400 backdrop-blur-sm transform transition-all duration-300 hover:scale-[1.02] focus:scale-[1.02]"
                aria-label="Access Code"
                disabled={isLoading}
                autoCapitalize="characters"
              />
            </div>
          </div>

          {error && <p className="text-lg font-semibold text-red-400">{error}</p>}

          <div>
            <button
              type="submit"
              className="w-full flex justify-center items-center gap-3 px-8 py-5 text-xl font-bold text-white bg-gradient-to-r from-green-500 to-green-600 border border-transparent rounded-2xl shadow-xl hover:from-green-600 hover:to-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:bg-gray-500 disabled:cursor-not-allowed transition-all duration-300 transform hover:scale-105 group"
              disabled={isLoading}
            >
              {isLoading ? (
                'Verifying...'
              ) : (
                <>
                  Enter Presentation <ArrowRight size={20} />
                </>
              )}
            </button>
          </div>
        </form>
        <div className="flex items-center justify-center text-sm text-gray-400 mt-8">
            <ShieldCheck className="w-5 h-5 mr-3 text-green-400" />
            <span className="font-medium italic">Secure Access Portal</span>
        </div>
        </div>
      </div>
      </div>

      {/* Animated Highway and Car */}
      <div className="absolute bottom-0 left-0 right-0">
        <div className="relative h-32 bg-gradient-to-t from-gray-900 to-gray-800">
          {/* Road Surface */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-800/50 to-gray-900"></div>
          
          {/* Road Markings */}
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-yellow-500 transform -translate-y-1/2 opacity-80"></div>
          <div className="absolute top-1/2 left-0 right-0 transform -translate-y-1/2 flex">
            {[...Array(20)].map((_, i) => (
              <div key={i} className="flex-1 h-1 bg-white mx-2 opacity-60"></div>
            ))}
          </div>
          
          {/* Animated Red Car */}
          <div className="absolute bottom-8" style={{ animation: 'drive 12s linear infinite' }}>
            <div className="relative group">
              <svg width="60" height="30" viewBox="0 0 120 60" className="transform hover:scale-110 transition-transform">
                {/* Main Car Body */}
                <ellipse cx="60" cy="35" rx="45" ry="18" fill="#e53e3e" />
                <ellipse cx="60" cy="33" rx="40" ry="15" fill="#dc2626" />
                
                {/* Car Top/Roof */}
                <ellipse cx="60" cy="25" rx="30" ry="12" fill="#dc2626" />
                <ellipse cx="60" cy="23" rx="25" ry="10" fill="#b91c1c" />
                
                {/* Windows */}
                <ellipse cx="50" cy="20" rx="12" ry="8" fill="#1f2937" opacity="0.8" />
                <ellipse cx="70" cy="20" rx="10" ry="8" fill="#1f2937" opacity="0.8" />
                
                {/* Side Window */}
                <ellipse cx="85" cy="25" rx="6" ry="5" fill="#f87171" />
                
                {/* Wheels */}
                <circle cx="35" cy="45" r="8" fill="#374151" />
                <circle cx="35" cy="45" r="5" fill="#6b7280" />
                <circle cx="35" cy="45" r="2" fill="#9ca3af" />
                
                <circle cx="85" cy="45" r="8" fill="#374151" />
                <circle cx="85" cy="45" r="5" fill="#6b7280" />
                <circle cx="85" cy="45" r="2" fill="#9ca3af" />
                
                {/* Headlights */}
                <ellipse cx="105" cy="30" rx="4" ry="3" fill="#fef3c7" />
                <ellipse cx="105" cy="38" rx="4" ry="3" fill="#fef3c7" />
              </svg>
              {/* Headlight Glow */}
              <div className="absolute -right-1 top-2 w-4 h-3 bg-yellow-300 rounded-l-full opacity-60 blur-sm"></div>
              <div className="absolute -right-1 top-5 w-4 h-3 bg-yellow-300 rounded-l-full opacity-60 blur-sm"></div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        @keyframes drive {
          from { left: -100px; }
          to { left: 100%; }
        }
      `}</style>
    </div>
  );
}

export default AccessCodePage;